import React, { createContext, useState, useEffect } from "react";
import { isTokenValid, getUserDataFromToken } from "../utils/tokenUtils";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [user, setUser] = useState(null); 

  useEffect(() => {
    const checkTokenValidity = () => {
      const token = localStorage.getItem("token");
      
      if (!token) {
        setIsAuthenticated(false);
        setUser(null);
        setIsAuthLoading(false);
        return;
      }

      // Check if token is valid and not expired
      if (isTokenValid(token)) {
        setIsAuthenticated(true);
        const userData = getUserDataFromToken(token);
        setUser(userData);
      } else {
        // Token is expired or invalid, clean up
        console.log("Token expired or invalid, logging out...");
        localStorage.removeItem("token");
        setIsAuthenticated(false);
        setUser(null);
      }
      
      setIsAuthLoading(false);
    };

    checkTokenValidity();

    // Check token validity periodically (every 5 minutes)
    const intervalId = setInterval(checkTokenValidity, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, []);

  const login = (token) => {
    if (isTokenValid(token)) {
      localStorage.setItem("token", token);
      setIsAuthenticated(true);
      const userData = getUserDataFromToken(token);
      setUser(userData);
      
      // Dispatch custom event to notify other contexts
      window.dispatchEvent(new Event('user-logged-in'));
    } else {
      console.error("Attempted to login with invalid or expired token");
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setIsAuthenticated(false);
    setUser(null);
  };

  // Function to manually check and refresh auth state
  const checkAuthState = () => {
    const token = localStorage.getItem("token");
    if (token && isTokenValid(token)) {
      setIsAuthenticated(true);
      const userData = getUserDataFromToken(token);
      setUser(userData);
    } else {
      localStorage.removeItem("token");
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      isAuthLoading, 
      user,
      login, 
      logout, 
      checkAuthState 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
